import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { shopApi } from "@/lib/api";

export default function WallOfLove({ limit = 6 }) {
  const { data = [], isLoading } = useQuery({
    queryKey: ["reviewsWall", limit],
    queryFn: () => shopApi.reviewsWall(limit),
    staleTime: 5 * 60 * 1000,
  });
  if (!isLoading && data.length === 0) return null;

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 border-t border-white/5" data-testid="wall-of-love">
      <div className="text-center mb-10">
        <p className="chip mx-auto mb-3 border-primary/40 text-primary">
          <Star className="h-3 w-3" fill="currentColor" /> Loved by shoppers
        </p>
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
          What our customers are saying
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {data.slice(0, limit).map((r, i) => (
          <motion.div
            key={r.review_id}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
            className="rounded-2xl bg-card border border-white/5 hover:border-primary/40 transition-all p-5 flex flex-col"
            data-testid={`review-card-${r.review_id}`}
          >
            <Quote className="h-6 w-6 text-primary/60 mb-2" />
            <div className="flex gap-0.5 mb-2">
              {Array.from({ length: 5 }).map((_, j) => (
                <Star key={j} className={`h-3.5 w-3.5 ${j < r.rating ? "text-primary" : "text-white/10"}`} fill={j < r.rating ? "currentColor" : "none"} />
              ))}
            </div>
            {r.title && <p className="font-semibold text-sm mb-1">{r.title}</p>}
            <p className="text-sm text-muted-foreground line-clamp-4 flex-1">{r.body}</p>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
              <span className="text-xs text-muted-foreground">— {r.user_name}</span>
              {r.product?.slug && (
                <Link to={`/product/${r.product.slug}`} className="flex items-center gap-2 text-xs text-primary hover:underline">
                  <img src={r.product.images?.[0]} alt="" className="h-6 w-6 rounded object-cover" />
                  <span className="max-w-[130px] truncate">{r.product.title}</span>
                </Link>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
